/**
 * Mini strike-zone overlay for the pitches in a single at-bat. The rule-book
 * zone is drawn from the per-pitch sz_top/sz_bot when present (Statcast
 * reports them in feet from ground); a sensible 1.5–3.5 ft fallback fills
 * in when those fields are missing. Pitches are plotted with plate_x
 * (horizontal, feet from plate center, catcher's view) and plate_z
 * (vertical, feet from ground).
 *
 * Failure isolation: returns null when there is no pitch with non-null
 * plate_x AND plate_z. Older fixtures / non-tracked pitches simply yield
 * no zone — no crash, no console noise. Callers should also filter their
 * input but defense-in-depth here is cheap.
 */
import type { PitchEntry } from '../state/selectors';
import styles from './StrikeZone.module.css';

interface StrikeZoneProps {
  pitches: PitchEntry[];
  /** When set, the SVG is wrapped in a button that fires this callback (zoom-in). */
  onZoom?: () => void;
  /** When true, render at the larger "expanded" size (used inside ZoomModal). */
  expanded?: boolean;
}

// SVG canvas: 100×120 viewBox.
// Horizontal: map plate_x ∈ [-1.5, +1.5] ft → x ∈ [10, 90]
// Vertical:   map plate_z ∈ [0, 5] ft       → y ∈ [110, 10]  (top of canvas = high)
const X_MIN = -1.5;
const X_MAX = 1.5;
const Z_MIN = 0;
const Z_MAX = 5;
const X_PIX_MIN = 10;
const X_PIX_MAX = 90;
const Y_PIX_TOP = 10;
const Y_PIX_BOT = 110;

function mapX(plateX: number): number {
  const clamped = Math.max(X_MIN, Math.min(X_MAX, plateX));
  return X_PIX_MIN + ((clamped - X_MIN) / (X_MAX - X_MIN)) * (X_PIX_MAX - X_PIX_MIN);
}

function mapY(plateZ: number): number {
  const clamped = Math.max(Z_MIN, Math.min(Z_MAX, plateZ));
  // Y increases downward in SVG; high pitches sit near the top of the canvas.
  return Y_PIX_BOT - ((clamped - Z_MIN) / (Z_MAX - Z_MIN)) * (Y_PIX_BOT - Y_PIX_TOP);
}

// Rule-book horizontal zone bounds: ±0.85 ft (Statcast convention).
const ZONE_X_LEFT = -0.85;
const ZONE_X_RIGHT = 0.85;
const ZONE_Z_FALLBACK_TOP = 3.5;
const ZONE_Z_FALLBACK_BOT = 1.5;

function pitchFill(p: PitchEntry): string {
  // Prefer the GUMBO result (always present) and fall back to the Statcast
  // description when GUMBO is unhelpful.
  const candidates = [p.result, p.description].filter(Boolean) as string[];
  for (const c of candidates) {
    const d = c.toLowerCase();
    if (d.includes('in play')) return 'var(--c-warn, #f59e0b)';
    if (d.includes('strike') || d.includes('foul')) return 'var(--c-ink-80, #2b3142)';
    if (d.includes('ball')) return 'var(--c-accent, #2563eb)';
  }
  return 'var(--c-ink-50, #6c7385)';
}

export default function StrikeZone({ pitches, onZoom, expanded = false }: StrikeZoneProps): JSX.Element | null {
  const valid = pitches.filter(
    (p) => typeof p.plateX === 'number' && typeof p.plateZ === 'number',
  );
  if (valid.length === 0) return null;

  // Use the first pitch's strike-zone bounds — they're per-batter and rarely
  // vary across one at-bat. Fall back to rule-book defaults when absent.
  const sample = valid[0]!;
  const szTop = typeof sample.szTop === 'number' ? sample.szTop : ZONE_Z_FALLBACK_TOP;
  const szBot = typeof sample.szBot === 'number' ? sample.szBot : ZONE_Z_FALLBACK_BOT;

  const zoneLeftPx = mapX(ZONE_X_LEFT);
  const zoneRightPx = mapX(ZONE_X_RIGHT);
  const zoneTopPx = mapY(szTop);
  const zoneBotPx = mapY(szBot);

  const ariaLabel = `Pitch locations — ${valid.length} pitch${valid.length === 1 ? '' : 'es'}`;
  const svgClass = expanded ? `${styles.svg} ${styles.svgExpanded ?? ''}` : styles.svg;
  const inner = (
    <>
      <svg
        viewBox="0 0 100 120"
        className={svgClass}
        role="img"
        aria-label={ariaLabel}
      >
        {/* Frame */}
        <rect
          x={X_PIX_MIN}
          y={Y_PIX_TOP}
          width={X_PIX_MAX - X_PIX_MIN}
          height={Y_PIX_BOT - Y_PIX_TOP}
          fill="var(--c-surface-2, #f4f6fb)"
          stroke="var(--c-divider, #dee3ee)"
          strokeWidth={0.5}
        />
        {/* Strike-zone rectangle (per-batter bounds) */}
        <rect
          x={zoneLeftPx}
          y={zoneTopPx}
          width={zoneRightPx - zoneLeftPx}
          height={zoneBotPx - zoneTopPx}
          fill="none"
          stroke="var(--c-ink-70, #3d4456)"
          strokeWidth={0.9}
          data-testid="strike-zone-rect"
        />
        {/* Thirds (third-third grid inside the zone) */}
        {[1, 2].map((i) => {
          const x = zoneLeftPx + ((zoneRightPx - zoneLeftPx) / 3) * i;
          return (
            <line
              key={`v${i}`}
              x1={x}
              y1={zoneTopPx}
              x2={x}
              y2={zoneBotPx}
              stroke="var(--c-divider-strong, #c4cad8)"
              strokeWidth={0.3}
              opacity={0.6}
            />
          );
        })}
        {[1, 2].map((i) => {
          const y = zoneTopPx + ((zoneBotPx - zoneTopPx) / 3) * i;
          return (
            <line
              key={`h${i}`}
              x1={zoneLeftPx}
              y1={y}
              x2={zoneRightPx}
              y2={y}
              stroke="var(--c-divider-strong, #c4cad8)"
              strokeWidth={0.3}
              opacity={0.6}
            />
          );
        })}
        {/* Pitches — most recent gets a slightly larger, accented marker */}
        {valid.map((p, i) => {
          const isRecent = i === valid.length - 1;
          const cx = mapX(p.plateX as number);
          const cy = mapY(p.plateZ as number);
          return (
            <g key={p.playId ?? i} data-testid="strike-zone-pitch" data-recent={isRecent ? 'true' : 'false'}>
              <circle
                cx={cx}
                cy={cy}
                r={isRecent ? 3.4 : 2.6}
                fill={pitchFill(p)}
                stroke="var(--c-ink-0, #fff)"
                strokeWidth={0.6}
                opacity={isRecent ? 1 : 0.85}
              />
              <text
                x={cx}
                y={cy + 1}
                textAnchor="middle"
                fontSize={2.6}
                fill="var(--c-ink-0, #fff)"
                fontWeight={700}
              >
                {p.index ?? i + 1}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className={styles.caption}>
        {valid.length} pitch{valid.length === 1 ? '' : 'es'}
      </figcaption>
    </>
  );

  if (onZoom) {
    return (
      <button
        type="button"
        className={`${styles.wrap} ${styles.zoomable ?? ''}`}
        onClick={onZoom}
        aria-label={`Expand: ${ariaLabel}`}
        data-testid="strike-zone"
      >
        {inner}
      </button>
    );
  }

  return (
    <figure className={styles.wrap} data-testid="strike-zone">
      {inner}
    </figure>
  );
}
